import { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import { api } from '@/api/growth/dashboard/api';
import {
    GrowthDashboardLightInterceptionCardInfo,
    GrowthDashboardReq,
} from '@/api/growth/dashboard/type';
import Tooltip from '@components/common/Tooltip';

interface GrowthLightInterceptionInfoCardProps {
    gfIdx: number;
}

const GrowthLightInterceptionInfoCard = ({
    gfIdx,
}: GrowthLightInterceptionInfoCardProps) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<ApexCharts | null>(null);
    const [loading, setLoading] = useState(true);
    const [lightInterceptionInfo, setLightInterceptionInfo] =
        useState<GrowthDashboardLightInterceptionCardInfo | null>(null);

    // 수광효율 데이터 조회
    useEffect(() => {
        if (gfIdx) {
            fetchLightInterceptionInfo(gfIdx);
        }
    }, [gfIdx]);

    const fetchLightInterceptionInfo = async (gfIdx: number) => {
        try {
            setLoading(true);
            const req: GrowthDashboardReq = { gfIdx };
            const response = await api.getLightInterceptionCardInfo(req);
            setLightInterceptionInfo(response.data);
        } catch (error) {
            console.error('수광효율 정보 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    // 차트 렌더링
    useEffect(() => {
        if (
            !chartRef.current ||
            !lightInterceptionInfo ||
            lightInterceptionInfo.list.length === 0
        )
            return;

        // 기존 차트 인스턴스 제거
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // 수광효율 값 배열 추출
        const lightInterceptionValues = lightInterceptionInfo.list.map(
            item => item.lightInterception,
        );

        const options = {
            series: [
                {
                    name: '수광효율',
                    data: lightInterceptionValues,
                },
            ],
            chart: {
                type: 'area',
                height: 70,
                sparkline: {
                    enabled: true,
                },
                dropShadow: {
                    enabled: true,
                    enabledOnSeries: undefined,
                    top: 0,
                    left: 0,
                    blur: 3,
                    color: 'rgba(var(--info-rgb))',
                    opacity: 0.4,
                },
            },
            stroke: {
                curve: 'smooth', // 곡선
                width: 1,
            },
            fill: {
                type: 'gradient',
                gradient: {
                    opacityFrom: 0.5,
                    opacityTo: 0.2,
                    stops: [0, 60],
                    colorStops: [
                        [
                            {
                                offset: 0,
                                color: 'rgba(var(--info-rgb))',
                                opacity: 0.3,
                            },
                            {
                                offset: 60,
                                color: 'rgba(var(--info-rgb))',
                                opacity: 0.2,
                            },
                        ],
                    ],
                },
            },
            colors: ['rgba(var(--info-rgb))'],
            tooltip: {
                fixed: {
                    enabled: false,
                },
                x: {
                    show: false,
                },
                y: {
                    title: {
                        formatter: function () {
                            return '';
                        },
                    },
                    formatter: function (value: number) {
                        return value.toFixed(2) + '%';
                    },
                },
            },
        };

        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [lightInterceptionInfo]);

    // 차이 표시 계산 (증가/감소)
    const getDifferenceDisplay = () => {
        if (
            !lightInterceptionInfo ||
            lightInterceptionInfo.lightInterceptionDifference === null
        )
            return null;

        const diff = lightInterceptionInfo.lightInterceptionDifference;
        const isPositive = diff >= 0;
        const icon = isPositive
            ? 'ri-arrow-left-up-line'
            : 'ri-arrow-left-down-line';

        return (
            <span className="badge bg-info rounded-pill align-items-center fs-11">
                <i className={`${icon} fs-11`}></i> {Math.abs(diff).toFixed(2)}%
            </span>
        );
    };

    if (loading) {
        return (
            <div className="card custom-card overflow-hidden">
                <div className="card-body">
                    <div className="d-flex align-items-center justify-content-center">
                        <div
                            className="spinner-border text-secondary"
                            role="status"
                        >
                            <span className="visually-hidden">로딩중...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!lightInterceptionInfo) {
        return (
            <div
                className="card custom-card overflow-hidden"
                style={{ height: 139 }}
            >
                <div className="card-body">
                    <div className="text-center text-muted d-flex flex-column justify-content-center h-100 gap-2 ">
                        수광효율 데이터를 불러올 수 없습니다.
                        <br />
                        (콩 등의 특정작물에만 노출됩니다)
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card custom-card overflow-hidden">
            <div className="">
                <div className="card-body">
                    <div className="d-flex align-items-center w-100 justify-content-between gap-1">
                        <div>
                            <p className="mb-1 text-muted fw-medium">
                                평균 수광효율
                                <Tooltip
                                    content="수광효율은 작물이 받아들이는 빛의 비율을 나타냅니다. 수광효율이 높을수록 광합성 능력이 우수하여 생육이 양호합니다."
                                    customClass="tooltip-dark"
                                    placement="top"
                                >
                                    <i className="ri-question-line" />
                                </Tooltip>
                            </p>
                            <h4 className="mb-0 fw-medium">
                                {lightInterceptionInfo.recentAvgLightInterception
                                    ? lightInterceptionInfo.recentAvgLightInterception.toFixed(
                                          2,
                                      )
                                    : '0'}
                                <small className="fs-11">%</small>
                            </h4>
                        </div>
                        <div className="ms-auto text-end">
                            {getDifferenceDisplay()}
                            <div className="text-muted fs-12 mt-1">
                                2주전 대비
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    ref={chartRef}
                    id="light-interception-chart"
                    style={{
                        marginTop: -20,
                    }}
                ></div>
            </div>
        </div>
    );
};

export default GrowthLightInterceptionInfoCard;
